<script lang="ts" setup>
import ListFilter from '@/components/common/ListFilter.vue';
import UserForm from '@/components/users/UserForm.vue';
import UserRequestForm from '@/components/users/UserRequestForm.vue';
import { User, getUserType, UserLevel } from '@/models/user.model';
import { FilterData } from '@/services/users.service';
import { Mutations, ShiftCMSState } from '@/store';
import { UsersActions, UsersState } from '@/stores/users.store';
import { format } from 'date-fns';
import { computed, markRaw, onBeforeUnmount, onMounted, Ref, ref } from 'vue';
import { useStore } from 'vuex';

const store = useStore<ShiftCMSState & { us: UsersState }>();

const total = computed<number>(() => store.state.us.userTotal);
const users = computed<User[]>(() => store.state.us.users);
const token = computed(() => store.state.token);

const filterData = ref<FilterData>({
  search: '',
  level: null,
  page: 1,
  count: 25,
  sort: 'id',
  direction: 'asc',
});

const addUser = () => {
  store.commit(Mutations.ShowDrawer, { component: markRaw(UserForm) });
};

const editUser = (_: Event, { item }: { item: User }) => {
  store.commit(Mutations.ShowDrawer, {
    component: markRaw(UserForm),
    data: item,
  });
};

const handleUserRequest = (_: Event, { item }: { item: User }) => {
  store.commit(Mutations.ShowDrawer, {
    component: markRaw(UserRequestForm),
    data: item,
  });
};

const update = () => {
  store.dispatch(UsersActions.GetUserList, filterData.value);
};

onMounted(() => {
  update();
});

onBeforeUnmount(() => {
  store.commit(Mutations.HideDrawer);
});

const tableType = ref<string>('one');

const headers = computed(() =>
  tableType.value === 'one'
    ? [
        { title: 'ID', key: 'id', width: '5%' },
        { title: 'Type', key: 'type', sortable: false, width: '5%' },
        { title: 'Name', key: 'name', width: '15%' },
        { title: 'Email', key: 'email', width: '19%' },
        { title: 'Quizzes', sortable: false, key: 'stories', width: '2%' },
        { title: 'List', sortable: false, key: 'actions', width: '2%' },
        { title: 'Registration', key: 'creationDate' },
        { title: 'Active', key: 'activeDate' },
        { title: 'Status', key: 'state' },
      ]
    : [
        { title: 'ID', key: 'id', sortable: false, width: '5%' },
        { title: 'Name', key: 'name', width: '15%' },
        { title: 'Email', key: 'email', width: '19%' },
        { title: 'Postalcode', key: 'postal_code', width: '10%' },
        { title: 'City', key: 'city', width: '10%' },
        { title: 'Registration', key: 'creationDate' },
      ],
);

const requestHeaders = [
  { title: 'ID', key: 'id', sortable: false, width: '5%' },
  { title: 'Email', key: 'email', width: '20%' },
  { title: 'Type', key: 'type', width: '75%' },
];

function updateFilter(filters: { filter: UserLevel; filterTitle: string }) {
  filterData.value = {
    ...filterData.value,
    level: filters.filter,
    search: filters.filterTitle,
  };
  resetPage();
  update();
}

function updateItemsPerPage(count: number) {
  filterData.value = { ...filterData.value, count };
  update();
}
function updatePage(page: number) {
  filterData.value = { ...filterData.value, page };
  update();
}

function resetPage() {
  filterData.value = { ...filterData.value, page: 1 };
}
function updateSortBy(sorting: { key: string; order: 'asc' | 'desc' }[]) {
  if (sorting.length) {
    const { key, order } = sorting[0];

    filterData.value = { ...filterData.value, sort: key, direction: order };
    resetPage();
    update();
  }
}

const ids = ref<string>('');

const parseUsers = () => {
  ids.value = ids.value.replace(/[\n|\s]/g, ',');
};
const downloadUsers = (isActive: Ref) => {
  window.open(`/api/user-csv/${ids.value}?token=${token.value}`, 'new');

  isActive.value = false;
};
</script>
<template>
  <v-container>
    <h2>Users</h2>
    <ListFilter
      :options="[
        { label: 'Admin', value: UserLevel.Admin },
        { label: 'Editor', value: UserLevel.ContentEditor },
        { label: 'User', value: UserLevel.RegisteredUser },
        { label: 'Device', value: UserLevel.Device },
      ]"
      @filter="updateFilter"
    >
      <li class="filter">
        <v-btn
          flat
          density="compact"
          :color="tableType === 'one' ? 'secondary' : ''"
          @click="tableType = tableType === 'one' ? 'two' : 'one'"
          >{{ tableType === 'one' ? 'User' : 'Email' }} list</v-btn
        >
      </li>
    </ListFilter>
    <v-data-table-server
      density="compact"
      @click:row="editUser"
      :headers="headers"
      :items="users"
      :itemsLength="total"
      :itemsPerPage="filterData.count"
      :page="filterData.page"
      @update:itemsPerPage="updateItemsPerPage"
      @update:page="updatePage"
      @update:sortBy="updateSortBy"
      hover
    >
      <template v-slot:item.name="{ item }">
        {{ item.firstName }} {{ item.lastName }}
      </template>
      <template v-slot:item.type="{ item }">
        {{ getUserType(item) }}
      </template>
      <template v-slot:item.stories="{ item }">
        {{ item.stories.filter((s) => s.watchedDate).length }}
      </template>
      <template v-slot:item.actions="{ item }">
        {{ item.actions.length + item.initiatives.length }}
      </template>
      <template v-slot:item.creationDate="{ value }">
        <span class="date">{{ format(value, 'd/M/yyyy - HH:mm') }}</span>
      </template>
      <template v-slot:item.activeDate="{ value }">
        <span class="date">{{
          value ? format(value, 'd/M/yyyy - HH:mm') : '-'
        }}</span>
      </template>
      <template v-slot:item.postal_code="{ item }">
        {{ item.address?.postal_code }}
      </template>
      <template v-slot:item.city="{ item }">
        {{ item.address?.city }}
      </template>
      <template v-slot:item.state="{ item }">
        <v-chip
          variant="flat"
          density="compact"
          color="info"
          v-if="item.level === UserLevel.Admin && item.twoFactorEnabled"
          >2FA</v-chip
        >
        <v-chip
          variant="flat"
          density="compact"
          color="warning"
          v-if="item.level === UserLevel.Admin && !item.twoFactorEnabled"
          >No 2FA</v-chip
        >
      </template>
    </v-data-table-server>

    <v-btn @click="addUser" color="success">Add User</v-btn>

    <v-dialog width="auto">
      <template v-slot:activator="{ props: activatorProps }">
        <v-btn class="ml-4" v-bind="activatorProps" color="success"
          >Download CSV</v-btn
        >
      </template>
      <template v-slot:default="{ isActive }">
        <v-card>
          <v-card-text>
            <h2>Download CSV</h2>
            <p>Provide IDS of users you wish the download.</p>
            <v-text-field
              v-model="ids"
              label="1, 2, 4, 1029... "
              hint="(spaces or linebreaks are also allowed)"
              @keyup="parseUsers"
            />
            <v-btn
              class="mt-2"
              color="success"
              type="button"
              block
              @click="() => downloadUsers(isActive)"
              >Download
              {{
                ids.length ? `${ids}`.split(',').length + ' users' : ''
              }}</v-btn
            >
          </v-card-text>
        </v-card>
      </template>
    </v-dialog>

    <div
      class="mt-8"
      v-if="
        users.filter(
          (u) => u.requestedAccountDeletion || u.requestedDataRemoval,
        ).length
      "
    >
      <v-alert type="warning"
        >Users that want their data or account deleted.</v-alert
      >
      <v-data-table
        class="mt-8"
        @click:row="handleUserRequest"
        density="compact"
        :headers="requestHeaders"
        :items="
          users.filter(
            (u) => u.requestedAccountDeletion || u.requestedDataRemoval,
          )
        "
        itemsPerPage="25"
        hover
      >
        <template v-slot:item.type="{ item }">
          {{ item.requestedAccountDeletion ? 'Delete account' : 'Delete data' }}
        </template>
      </v-data-table>
    </div>
  </v-container>
</template>
<style scoped>
.date {
  font-size: 70%;
}
</style>
